// PROD-3183 (ROS, PE and risk part) — where each finding the room said goes, as a SUGGESTION.
//
// Andrew's rule (9 Oct): when the section has a template option with the same value, suggest ticking
// it; otherwise suggest adding the text to that section's notes. Nothing is written until the user
// accepts. Everything is a suggestion; the user chooses.
//
// The server sends three lists on every Soap frame (Video #232, `ClinicalFindingDto`): `rosFindings`,
// `examFindings` and `riskFindings`, each item `{ category, finding, value, assertion, speaker }`.
// `assertion` is one of `AmbientWireValues.Assertion` (Scrips.Core #101): "present", "absent",
// "normal", or "" (not stated, never read as absent or normal). The lists can be empty or missing
// (the prompts are not published yet, and the AOAI fallback omits the fields): both mean nothing.
//
// Pure. No React, no network.
//
// STRICT ON PURPOSE. A tick is suggested only when exactly one option in the section says the same
// thing, compared on normalised text AND negation. Anything else (no match, more than one, a value a
// tick cannot carry, an answer the clinician already gave differently, an unstated assertion) becomes
// a line for the section's notes, in the speaker's words. A wrong tick is a wrong record; a note line
// is the same words the room said.

import { qrIsNormalChip, qrItemEnabled, qrOptionValue, type QAnswerOption, type QAnswerValue, type QItem, type Questionnaire, type QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { AmbientClinicalFinding, SoapPayload } from './ambient-types';
import type { DecisionIdentity } from './decision-types';
import { FINDING_DECISION_KINDS } from './written-entity';
import { findNarrativeItem, questionnaireForStep, type QuestionnaireStep } from '../lib/questionnaire-groups';

/** The three sections a finding can be placed in, by the list it arrived in. */
export type FindingSection = 'ros' | 'exam' | 'risk';

export const FINDING_SECTIONS: readonly FindingSection[] = ['ros', 'exam', 'risk'];

/**
 * The decision kind each section's suggestions are recorded under (`proposalKind`). The server's
 * action register must hold an active row for each, or the record is refused with 400
 * `unknown-action-kind` (the write itself still stands, and the item says so).
 */
export const FINDING_KIND: Readonly<Record<FindingSection, string>> = FINDING_DECISION_KINDS;

/** The encounter step (template section) each list belongs to. */
export const FINDING_STEP: Readonly<Record<FindingSection, QuestionnaireStep>> = { ros: 'ros', exam: 'pe', risk: 'risk' };

/** The section headings the encounter uses (PROD-2011). */
export const FINDING_SECTION_LABEL: Readonly<Record<FindingSection, string>> = {
  ros: 'Review of Systems',
  exam: 'Physical Exam',
  risk: 'Risk Assessment',
};

const LIST_OF: Readonly<Record<FindingSection, keyof SoapPayload>> = {
  ros: 'rosFindings',
  exam: 'examFindings',
  risk: 'riskFindings',
};

/** The assertion words (`AmbientWireValues.Assertion`). Anything else counts as not stated. */
export const ASSERTION = { present: 'present', absent: 'absent', normal: 'normal' } as const;

/** Why a finding is offered as a notes line rather than a tick. Shown to the clinician. */
export type NotesReason = 'no-match' | 'ambiguous' | 'not-stated' | 'value' | 'conflict';

export type FindingPlacement =
  /** Tick one option: a boolean finding (`true` / `false`) or a choice option's key. */
  | {
      kind: 'tick';
      linkId: string;
      value: boolean | string;
      /** The choice item takes several answers: the option is ADDED to the ones already chosen. */
      repeats: boolean;
      /** What the clinician sees: the option as the template words it, with its state. */
      label: string;
    }
  /** Add the line to the section's notes (its `-narrative` item). */
  | { kind: 'notes'; linkId: string; line: string; reason: NotesReason }
  /** The section has no notes field in this template (or no section at all): nothing to add to. */
  | { kind: 'nowhere'; line: string; reason: NotesReason };

export interface FindingSuggestion {
  section: FindingSection;
  /** How the decision endpoints name it. Stable across re-analyses of the same words. */
  identity: DecisionIdentity;
  /** The words the room said, verbatim. */
  heard: string;
  speaker?: string;
  placement: FindingPlacement;
  /** The section already holds it: the option is ticked that way, or the line is in the notes. */
  already: boolean;
}

/** linkIds a suggestion must never write: answers that are not this section's questionnaire answers. */
export interface PlacementScope {
  /** Items whose answers go to another store (the Risk bridge's Fall / Pain groups, free screens). */
  excluded?: ReadonlySet<string>;
  /** Items and options the template leaves out for this patient (`PatientExclusions`). */
  patientExcluded?: { items: ReadonlySet<string>; options: ReadonlyMap<string, ReadonlySet<string>> };
}

const MAX_KEY = 512;
const MAX_LABEL = 512;
const clip = (s: string, max: number) => (s.length > max ? s.slice(0, max) : s);

// ── Normalising text ────────────────────────────────────────────────────────────────────────

/** One word, singular: "wheezes" → "wheeze", "allergies" → "allergy". Simple plurals only. */
function singular(word: string): string {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && /(sh|ch|x|z|ss)es$/.test(word)) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s') && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
}

/**
 * Case, punctuation, whitespace and simple plurals removed: "Chest Pains." and "chest pain" are the
 * same text. "&" reads as "and". Digits stay ("20 pack years").
 */
export function normaliseFindingText(text: string | undefined): string {
  return (text ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(singular)
    .join(' ');
}

/**
 * Leading words that say "not there", as they read AFTER normalising ("denies" is "deny"). Longest
 * first, so "no evidence of" wins over "no".
 */
const NEGATIONS = ['negative for', 'no evidence of', 'no sign of', 'deny', 'denied', 'without', 'absent', 'never', 'not', 'no'];

/**
 * The negations a TEMPLATE LABEL is split on. Not "never" and not "not": in a label they are part of
 * the option's own meaning, not a "no" in front of it. "Never smoker" is a smoking status, not "No
 * smoker" (a former smoker is not a smoker either, and must not tick it); "Not applicable" is an
 * answer, not "no applicable".
 */
const LABEL_NEGATIONS = NEGATIONS.filter((n) => n !== 'never' && n !== 'not');

/** Normalised text split into what it is about and whether it says it is NOT there. */
export function splitNegation(normalised: string, negations: readonly string[] = NEGATIONS): { core: string; negated: boolean } {
  for (const n of negations) {
    if (normalised.startsWith(`${n} `)) return { core: normalised.slice(n.length + 1).trim(), negated: true };
  }
  return { core: normalised, negated: false };
}

/** A template label split on the label negations only (see `LABEL_NEGATIONS`). */
const splitLabel = (form: string) => splitNegation(form, LABEL_NEGATIONS);

/**
 * The forms a template label can be matched by: the whole label, and for "Ringing in ears
 * (tinnitus)" also "Ringing in ears" and "tinnitus". Each normalised.
 */
function labelForms(label: string | undefined): string[] {
  const raw = label ?? '';
  const forms = new Set<string>([normaliseFindingText(raw)]);
  const paren = /^(.*?)\(([^)]*)\)(.*)$/.exec(raw);
  if (paren) {
    forms.add(normaliseFindingText(`${paren[1]} ${paren[3]}`));
    forms.add(normaliseFindingText(paren[2]));
  }
  forms.delete('');
  return [...forms];
}

const optionLabel = (o: QAnswerOption) => o.valueCoding?.display ?? o.valueString ?? o.valueCoding?.code ?? '';

// ── What a finding says ───────────────────────────────────────────────────────────────────────

interface Heard {
  /** Normalised, every negation word removed: what an ABSENT finding is about ("never smoked" → "smoked"). */
  core: string;
  /** Normalised, nothing removed: what a PRESENT or NORMAL finding says ("never smoker"). */
  full: string;
  /** The words start with a label negation ("no", "denies", …; not "never" / "not"). */
  labelNegated: boolean;
  /** "present" | "absent" | "normal" | "" (not stated). */
  assertion: string;
  /** The finding's own words carry a negation ("no chest pain"). */
  textNegated: boolean;
}

function heardOf(f: AmbientClinicalFinding): Heard {
  const assertion = (f.assertion ?? '').trim().toLowerCase();
  const full = normaliseFindingText(f.finding || f.category);
  const { core, negated } = splitNegation(full);
  return {
    core,
    full,
    labelNegated: splitLabel(full).negated,
    assertion: assertion === ASSERTION.present || assertion === ASSERTION.absent || assertion === ASSERTION.normal ? assertion : '',
    textNegated: negated,
  };
}

const upperFirst = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const lowerFirst = (s: string) => (s && !/^[A-Z]{2}/.test(s) ? s[0].toLowerCase() + s.slice(1) : s);
const squash = (s: string | undefined) => (s ?? '').trim().replace(/\s+/g, ' ');

/**
 * The finding as ONE notes line, in the speaker's words: "No chest pain", "Smokes: 20 pack-years",
 * "Chest: normal". The assertion is spelled out only when the words do not already carry it.
 */
export function findingLine(f: AmbientClinicalFinding): string {
  const finding = squash(f.finding) || squash(f.category);
  const value = squash(f.value);
  const heard = heardOf(f);
  let line = finding;
  if (heard.assertion === ASSERTION.absent && !heard.textNegated) line = `No ${lowerFirst(line)}`;
  if (heard.assertion === ASSERTION.normal && !/\bnormal\b/i.test(line) && !value) line = `${line}: normal`;
  if (value && !normaliseFindingText(line).includes(normaliseFindingText(value))) line = `${line}: ${value}`;
  return upperFirst(line);
}

/** The decision identity: the section's kind, and the finding's normalised words as the key. */
export function findingIdentity(section: FindingSection, f: AmbientClinicalFinding): DecisionIdentity {
  const key = [
    normaliseFindingText(f.category),
    heardOf(f).assertion,
    normaliseFindingText(f.finding),
    normaliseFindingText(f.value),
  ].join('|');
  return {
    proposalKind: FINDING_KIND[section],
    proposalKey: clip(key, MAX_KEY),
    proposalLabel: clip(findingLine(f), MAX_LABEL),
  };
}

// ── The section's options ─────────────────────────────────────────────────────────────────────

interface Candidate {
  linkId: string;
  value: boolean | string;
  repeats: boolean;
  label: string;
  /** Normalised titles of the groups above it, outermost first (for the category tie-break). */
  groups: string[];
}

/** Does this one label form say what was heard, and with which value? */
function booleanValueFor(form: string, item: QItem, h: Heard): boolean | undefined {
  const { core, negated } = splitLabel(form);
  if (negated) {
    // "No tenderness": ticked yes only by an ABSENT "tenderness". Never inferred the other way.
    return h.assertion === ASSERTION.absent && core === h.core ? true : undefined;
  }
  if (qrIsNormalChip(item)) {
    // A two-state "normal" finding ("Heart sounds normal") has no "no" state: only a yes.
    return (h.assertion === ASSERTION.normal || h.assertion === ASSERTION.present) && core === h.full ? true : undefined;
  }
  // Present: the label is exactly what was said. Absent: the label is what was said NOT to be there.
  if (h.assertion === ASSERTION.present) return core === h.full ? true : undefined;
  if (h.assertion === ASSERTION.absent) return core === h.core ? false : undefined;
  // "normal" on a present/absent finding ("cough: normal") says neither: no tick.
  return undefined;
}

function optionMatches(form: string, h: Heard): boolean {
  const { core, negated } = splitLabel(form);
  // "No chest pain": only an ABSENT chest pain. "Never smoker" is a positive option: only a finding
  // that SAYS "never smoker" (present), never an absent "smoker".
  if (negated) return h.assertion === ASSERTION.absent && core === h.core;
  return (h.assertion === ASSERTION.present || h.assertion === ASSERTION.normal) && core === h.full;
}

function candidatesFor(section: Questionnaire, h: Heard, scope: PlacementScope, values: Record<string, QAnswerValue>, notesLinkId?: string): Candidate[] {
  const out: Candidate[] = [];
  const excluded = scope.excluded;
  const patientItems = scope.patientExcluded?.items;
  const walk = (items: readonly QItem[], groups: string[]) => {
    for (const item of items) {
      if (excluded?.has(item.linkId) || patientItems?.has(item.linkId)) continue;
      if (!qrItemEnabled(item, values)) continue;
      if (item.type === 'group') {
        walk(item.item ?? [], [...groups, normaliseFindingText(item.text), normaliseFindingText(item.linkId)]);
        continue;
      }
      if (item.linkId === notesLinkId || item.readOnly || item.calculatedExpression) continue;
      if (item.type === 'boolean') {
        for (const form of labelForms(item.text)) {
          const value = booleanValueFor(form, item, h);
          if (value !== undefined) {
            const text = squash(item.text);
            // A present / absent finding names its state; a "normal" or "No X" finding is its own words.
            const twoWay = !qrIsNormalChip(item) && !splitLabel(form).negated;
            const label = twoWay ? `${text}: ${value ? 'present' : 'absent'}` : text;
            out.push({ linkId: item.linkId, value, repeats: false, label, groups });
            break;
          }
        }
      } else if ((item.type === 'choice' || item.type === 'open-choice') && item.answerOption?.length) {
        const offLimits = scope.patientExcluded?.options.get(item.linkId);
        for (const option of item.answerOption) {
          const key = qrOptionValue(option);
          if (offLimits?.has(key)) continue;
          if (labelForms(optionLabel(option)).some((form) => optionMatches(form, h))) {
            const question = squash(item.text);
            const label = squash(optionLabel(option));
            // The question's own words count for the category tie-break ("Smoking" for category "smoking").
            const path = [...groups, normaliseFindingText(item.text)];
            out.push({ linkId: item.linkId, value: key, repeats: !!item.repeats, label: question ? `${question}: ${label}` : label, groups: path });
          }
        }
      }
      if (item.item?.length) walk(item.item, groups);
    }
  };
  walk(section.item, []);
  // The same option reached twice (two label forms) is one candidate.
  const seen = new Set<string>();
  return out.filter((c) => {
    const k = `${c.linkId}|${String(c.value)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Two or more candidates: keep those under a group named like the finding's category, if any. */
function byCategory(cands: Candidate[], category: string | undefined): Candidate[] {
  const cat = normaliseFindingText(category);
  if (!cat || cands.length < 2) return cands;
  const under = cands.filter((c) => c.groups.some((g) => ` ${g} `.includes(` ${cat} `)));
  return under.length > 0 ? under : cands;
}

// ── What the section holds ────────────────────────────────────────────────────────────────────

const answerOf = (response: QuestionnaireResponse, linkId: string) => response.answers.find((a) => a.linkId === linkId)?.value;

/** The notes as lines, each normalised. */
const noteLines = (notes: string) => notes.split(/\r?\n/).map(normaliseFindingText).filter(Boolean);

/** Does the response already hold this placement? A tick: that value. A note: that line. */
export function responseCarries(response: QuestionnaireResponse, placement: FindingPlacement): boolean {
  if (placement.kind === 'nowhere') return false;
  const current = answerOf(response, placement.linkId);
  if (placement.kind === 'notes') {
    const want = normaliseFindingText(placement.line);
    return typeof current === 'string' && noteLines(current).includes(want);
  }
  if (placement.repeats) return Array.isArray(current) && current.map(String).includes(String(placement.value));
  return current === placement.value || (typeof placement.value === 'boolean' && String(current) === String(placement.value));
}

/** A tick would REPLACE an answer the clinician gave differently. Then it is not offered. */
function conflicts(response: QuestionnaireResponse, c: Candidate): boolean {
  if (c.repeats) return false;
  const current = answerOf(response, c.linkId);
  if (current === undefined || current === '' || (Array.isArray(current) && current.length === 0)) return false;
  return String(current) !== String(c.value);
}

/**
 * The response with the placement written. A tick sets the option (a multi-answer choice keeps the
 * options already chosen); a note line is appended on its own line. Nothing else changes.
 */
export function applyPlacement(response: QuestionnaireResponse, placement: FindingPlacement): QuestionnaireResponse {
  if (placement.kind === 'nowhere' || responseCarries(response, placement)) return response;
  const others = response.answers.filter((a) => a.linkId !== placement.linkId);
  const current = answerOf(response, placement.linkId);
  let value: QAnswerValue;
  if (placement.kind === 'notes') {
    const existing = typeof current === 'string' ? current.replace(/\s+$/, '') : '';
    value = existing ? `${existing}\n${placement.line}` : placement.line;
  } else if (placement.repeats) {
    value = [...(Array.isArray(current) ? current.map(String) : []), String(placement.value)];
  } else {
    value = placement.value;
  }
  return { ...response, answers: [...others, { linkId: placement.linkId, value }] };
}

/** Where one finding goes in this section, given what the section holds now. */
export function placeFinding(
  f: AmbientClinicalFinding,
  sectionQuestionnaire: Questionnaire | undefined,
  response: QuestionnaireResponse,
  scope: PlacementScope = {},
): FindingPlacement {
  const line = findingLine(f);
  const h = heardOf(f);
  const notesItem = sectionQuestionnaire ? findNarrativeItem(sectionQuestionnaire.item) : undefined;
  const notesUsable = !!notesItem && !scope.excluded?.has(notesItem.linkId) && !scope.patientExcluded?.items.has(notesItem.linkId);
  const toNotes = (reason: NotesReason): FindingPlacement =>
    notesUsable ? { kind: 'notes', linkId: notesItem!.linkId, line, reason } : { kind: 'nowhere', line, reason };

  if (!h.assertion) return toNotes('not-stated');
  // "denies chest pain" said as PRESENT contradicts itself: the words win, as a line.
  // "Never smoker" said as present is a status, not a contradiction: only a label negation counts.
  if (h.labelNegated && h.assertion !== ASSERTION.absent) return toNotes('no-match');
  if (!sectionQuestionnaire || !h.core) return toNotes('no-match');

  const values: Record<string, QAnswerValue> = {};
  for (const a of response.answers) values[a.linkId] = a.value;
  const cands = byCategory(candidatesFor(sectionQuestionnaire, h, scope, values, notesItem?.linkId), f.category);
  if (cands.length === 0) return toNotes('no-match');
  if (cands.length > 1) return toNotes('ambiguous');
  const [c] = cands;
  // A tick cannot carry an amount ("20 pack-years"): the line keeps it.
  const value = normaliseFindingText(f.value);
  if (value && !normaliseFindingText(f.finding).includes(value)) return toNotes('value');
  if (conflicts(response, c)) return toNotes('conflict');
  return { kind: 'tick', linkId: c.linkId, value: c.value, repeats: c.repeats, label: c.label };
}

/**
 * Every finding the server sent, as suggestions, in the order it sent them. Empty or missing lists
 * give none. A finding with no words at all is skipped; the same words twice are one suggestion.
 */
export function toFindingSuggestions(
  soap: SoapPayload | null | undefined,
  questionnaire: Questionnaire | undefined,
  response: QuestionnaireResponse,
  scope: PlacementScope = {},
): FindingSuggestion[] {
  if (!soap) return [];
  const out: FindingSuggestion[] = [];
  const seen = new Set<string>();
  for (const section of FINDING_SECTIONS) {
    const list = soap[LIST_OF[section]];
    if (!Array.isArray(list) || list.length === 0) continue;
    const sectionQ = questionnaire ? questionnaireForStep(questionnaire, FINDING_STEP[section]) : undefined;
    for (const f of list as AmbientClinicalFinding[]) {
      if (!f || !(squash(f.finding) || squash(f.category))) continue;
      const identity = findingIdentity(section, f);
      const k = `${identity.proposalKind}|${identity.proposalKey}`;
      if (seen.has(k)) continue;
      seen.add(k);
      const placement = placeFinding(f, sectionQ, response, scope);
      out.push({
        section,
        identity,
        heard: squash(f.finding) || squash(f.category),
        ...(f.speaker ? { speaker: f.speaker } : {}),
        placement,
        already: responseCarries(response, placement),
      });
    }
  }
  return out;
}
